import { PatientEncounter } from '@finpay-development/shared-platform/lib/esm/patient/components/models/patient-encounter';
import { PatientViewModel } from '@finpay-development/shared-platform/lib/esm/patient/components/models/patient-view-model';
import { PatientChampion } from '@finpay-development/shared-platform/lib/esm/patient/components/models/patient-champion';
import { Rule } from '@finpay-development/shared-platform/lib/esm/admin-configuration/models/rule';
import { RvkOutboundCallPayload } from './rvk-outbound-call-payload';

/** Minimal VOB shape used for insurance mapping (V3). */
export interface RvkVobSource {
  payor?: {
    payorId?: number;
    payorName?: string;
    inNetwork?: boolean;
  };
  innDeductible?: { remaining?: number };
  oonDeductible?: { remaining?: number };
  innOopm?: { total?: number };
  oonOopm?: { total?: number };
  innCoInsurance?: number;
  oonCoInsurance?: number;
  innClassifications?: Array<{ coInsurance?: number | null }>;
  oonClassifications?: Array<{ coInsurance?: number | null }>;
  // legacy V2 field names (fallback)
  inNetwDeductibleRemaining?: number;
  ooNetwDeductibleRemaining?: number;
  inNetwOopMax?: number;
  ooNetwOopMax?: number;
  inNetwVobClassifications?: Array<{ coInsurance?: number | null }>;
  ooNetwVobClassifications?: Array<{ coInsurance?: number | null }>;
}

export type PayersMapLike = Map<number, { payorName?: string }> | Record<string, { payorName?: string }> | null | undefined;

function toNumber(value: unknown, fallback = 0): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Round to nearest cent (2 decimal places). */
function roundToPenny(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function digitsOnly(phone: string | undefined | null): string {
  return (phone || '').replace(/\D/g, '');
}

function isoDate(birthDate: string | undefined | null): string {
  if (!birthDate) {
    return '';
  }
  return String(birthDate).split('T')[0];
}

function firstGuarantor(champions: PatientChampion[] | undefined): PatientChampion | undefined {
  return (champions || []).find((c) => c?.isGuarantor === true);
}

function resolveInsuranceName(vob: RvkVobSource | null | undefined, payersMap: PayersMapLike): string {
  const payorId = vob?.payor?.payorId;
  if (payorId != null && payersMap) {
    if (payersMap instanceof Map) {
      const row = payersMap.get(Number(payorId));
      if (row?.payorName) {
        return row.payorName;
      }
    } else {
      const row = (payersMap as Record<string, { payorName?: string }>)[String(payorId)];
      if (row?.payorName) {
        return row.payorName;
      }
    }
  }
  return vob?.payor?.payorName || '';
}

function isInNetwork(vob: RvkVobSource | null | undefined): boolean {
  return vob?.payor?.inNetwork !== false;
}

function remainingDeductible(vob: RvkVobSource | null | undefined): number {
  if (!vob) {
    return 0;
  }
  if (isInNetwork(vob)) {
    return toNumber(
      vob.innDeductible?.remaining ?? vob.inNetwDeductibleRemaining,
      0
    );
  }
  return toNumber(
    vob.oonDeductible?.remaining ?? vob.ooNetwDeductibleRemaining,
    0
  );
}

function oopmAmount(vob: RvkVobSource | null | undefined): number {
  if (!vob) {
    return 0;
  }
  if (isInNetwork(vob)) {
    return toNumber(vob.innOopm?.total ?? vob.inNetwOopMax, 0);
  }
  return toNumber(vob.oonOopm?.total ?? vob.ooNetwOopMax, 0);
}

function coinsPatientPercentage(vob: RvkVobSource | null | undefined): number {
  if (!vob) {
    return 0;
  }
  if (isInNetwork(vob)) {
    if (vob.innCoInsurance != null) {
      return toNumber(vob.innCoInsurance, 0);
    }
    const classifications = vob.innClassifications || vob.inNetwVobClassifications || [];
    const first = classifications.find((c) => c?.coInsurance != null);
    return toNumber(first?.coInsurance, 0);
  }
  if (vob.oonCoInsurance != null) {
    return toNumber(vob.oonCoInsurance, 0);
  }
  const classifications = vob.oonClassifications || vob.ooNetwVobClassifications || [];
  const first = classifications.find((c) => c?.coInsurance != null);
  return toNumber(first?.coInsurance, 0);
}

export function deriveOptimalFromRule(
  rule: Rule | null | undefined,
  pfrAmt: number
): { optimal_dp: number; optimal_monthly: number } | null {
  if (!rule || rule.ruleId == null) {
    return null;
  }
  const optimalDownPmt = toNumber(rule.optimalDownPmt, 0);
  const optimal_dp =
    rule.optimalDownPmtType === '%'
      ? (optimalDownPmt * pfrAmt) / 100
      : optimalDownPmt;
  const optimalTermNum = rule.optimalTerm ? parseInt(String(rule.optimalTerm), 10) : NaN;
  if (!Number.isFinite(optimalTermNum) || optimalTermNum <= 0) {
    return null;
  }
  const optimal_monthly =
    pfrAmt > optimal_dp ? (pfrAmt - optimal_dp) / optimalTermNum : 0;
  return {
    optimal_dp: roundToPenny(optimal_dp),
    optimal_monthly: roundToPenny(optimal_monthly),
  };
}

export function mapEncounterToRvkOutboundPayload(params: {
  encounter: PatientEncounter;
  selectedPatient?: PatientViewModel | null;
  vob?: RvkVobSource | null;
  payersMap?: PayersMapLike;
  matchingRule?: Rule | null;
}): RvkOutboundCallPayload {
  const { encounter, selectedPatient, vob, payersMap, matchingRule } = params;
  const patientContact = selectedPatient?.contact;
  const pfrAmt = roundToPenny(toNumber(encounter.adjustedPfrAmt ?? encounter.pfrAmt, 0));
  const guarantor = firstGuarantor(encounter.patientChampion);
  const has_3pg = !!guarantor;

  const payload: RvkOutboundCallPayload = {
    account_id: encounter.patientId,
    ioc_id: encounter.patientEncounterId,
    crm_account_id: encounter.clientsPatientAccountId || '',
    crm_ioc_id: encounter.clientsPatientIOCId || '',
    rc_code: `RC${encounter.riskClass?.riskClassId ?? ''}`,
    to_number: digitsOnly(patientContact?.primPhoneNum),
    first_name: patientContact?.firstName || '',
    last_name: patientContact?.lastName || '',
    dob: isoDate(patientContact?.birthDate),
    email: patientContact?.email || '',
    provider_name: encounter.clientName || '',
    department_name: encounter.facilityName || '',
    insurance_name: resolveInsuranceName(vob, payersMap),
    level_of_care: encounter.levelOfCare || '',
    pfr_total_amt: pfrAmt,
    remaining_ded_amount: roundToPenny(remainingDeductible(vob)),
    coins_patient_percentage: roundToPenny(coinsPatientPercentage(vob)),
    oopm_amount: roundToPenny(oopmAmount(vob)),
    has_3pg,
  };

  if (has_3pg && guarantor) {
    payload.first_name_3pg = guarantor.contact?.firstName || '';
    payload.last_name_3pg = guarantor.contact?.lastName || '';
    payload.relation_3pg = guarantor.relationshipToPatient || '';
    payload.email_3pg = guarantor.contact?.email || '';
  }

  const optimal = deriveOptimalFromRule(matchingRule, pfrAmt);
  if (optimal) {
    payload.optimal_dp = optimal.optimal_dp;
    payload.optimal_monthly = optimal.optimal_monthly;
  }

  return payload;
}
